import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { map, mergeMap, tap } from 'rxjs';
import { IconComponent } from '../icons/icon.component';
import { OrderedItemService } from '../../core/services/ordering/ordered-item.service';
import { OrderService } from '../../core/services/order.service';
import { ItemEditorService } from '../../core/services/item-editor.service';
import { MenuWrapperService } from '../../core/services/menu-wrapper.service';
import { Upsell } from '../../core/models/menu/upsell.model';
import { PanelItem } from '../../core/models/menu/panel-item.model';
import { SizePrice } from '../../core/models/menu/size-price.model';
import { OrderableItem } from '../../core/models/menu/orderable-item.model';
import { DeserializationUtils } from '../../core/utils/deserialization-utils';
import { IconNameType } from '../icons/icon-name-type.enum';
import { GTMService } from '../../core/services/gtm.service';
import { EcommerceTriggeringPoint } from '../../core/models/ecommerce/ecommerce-triggering-point.enum';
import { AuditService } from '../../core/services/audit.service';
import { Utils } from '../../core/utils/utils';

@Component({
  selector: 'cwo-upsell',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './upsell.component.html',
  styleUrl: './upsell.component.scss',
})
export class UpsellComponent implements OnInit {
  private httpClient = inject(HttpClient);
  menuWrapperService = inject(MenuWrapperService);
  orderedItemService = inject(OrderedItemService);
  orderService = inject(OrderService);
  itemEditorService = inject(ItemEditorService);
  gtmService = inject(GTMService);
  private auditService = inject(AuditService);
  private destroyRef = inject(DestroyRef);
  itemImg = computed(() => {
    if (this.panelItem?.pictures?.length) {
      return this.panelItem?.pictures[0];
    } else {
      return null;
    }
  });

  isInItemEditor = input<boolean>(false);
  triggeringArea = input.required<string>();
  upsell = input.required<Upsell>();
  handleSisterUpsellVisibility = input<boolean>(false);

  readonly upsellAdded = output<boolean>();

  panelItem!: PanelItem;
  selectedSizePrice!: SizePrice;
  imageUrl!: string | undefined;
  iconNameType = IconNameType;

  constructor() {}

  ngOnInit(): void {
    this.panelItem = this.menuWrapperService.convertUpsellToPanelItem(this.upsell());
    this.selectedSizePrice = this.menuWrapperService.getDefaultSizePrice(
      this.panelItem.sizePrices
    );
    this.imageUrl = this.menuWrapperService.getPanelItemPictureSrc(
      this.panelItem
    );
  }

  hasCalories = computed(() => {
    const sizePrices = this.panelItem?.sizePrices;
    if (!sizePrices) return null;
    const sizeWithCalories = sizePrices.find((s: SizePrice) => s.caloriesFrom != null );
    if (!sizeWithCalories) return null;
    return Utils.getCalorieDisplay(this.panelItem, sizeWithCalories); 
  });

  addToOrder() {
    const url = new URL(this.panelItem.links![0].href);
    const link = url.pathname + url.search;
    this.auditService.createAudit(() => `Add "${this.upsell()?.orderableItem?.item}" item to order button clicked`);

    return this.httpClient
      .get<OrderableItem>(link)
      .pipe(
        map((json) => DeserializationUtils.deserializeObj(json, OrderableItem)),
        tap((orderableItem) => {
          const orderedItem = this.orderedItemService.createOrderedItem(
            orderableItem,
            this.selectedSizePrice?.size ?? ''
          );

          this.orderService
            .addItemToOrder(orderedItem)
            .pipe(
              tap((orderedItem) => {
                this.gtmService.pushAddToCartEvent({
                  items: [orderedItem],
                  order: this.orderService.order(),
                  triggeringArea: this.triggeringArea(),
                  triggeringPoint: EcommerceTriggeringPoint.ADD_ITEM_BUTTON,
                });
                if(!this.isInItemEditor()) {
                  this.auditService.createAudit(() => 
                    `'${orderedItem.quantity} ${orderedItem.size} ${orderedItem.item}' item has been added to the order: ${JSON.stringify({
                      itemId: orderedItem.itemId,
                      isUpsell: true,
                      upsellType: "Order Based"
                    })}`
                  );
                }
              }),
              mergeMap(() => this.orderService.fetchOrder()),
              takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.upsellAdded.emit(true));
        })
      )
      .subscribe(() => {
        if(this.handleSisterUpsellVisibility()) this.itemEditorService.handleVisibleSisterUpsells(false);
      });
  }

  onImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }
}
